/**
 * MapIconFactory
 *
 * Builds Leaflet icons for the map (pins and station icons)
 * with small status labels so the map looks consistent across the app.
 *
 * summary:
 * - `createCustomPinIcon`: text label over a generic pin image
 * - `createCustomIcon`: station icon with status color and optional error badge
 * - Uses sizes and colors from `utils/constants` and station icon from `Map/stationIcons`
 */

import L from "leaflet";
import { getStationIcon } from "../../Map/stationIcons";
import {
  ICON_SIZES,
  STATUS_COLORS,
  STATUS_TEXT_COLORS,
} from "../utils/constants";

/**
 * Creates a custom pin icon with text label
 * @param {string} iconUrl - URL of the icon image
 * @param {string} text - Text to display on the icon
 * @param {string} color - Color of the text
 * @returns {L.DivIcon} Leaflet div icon
 */
export const createCustomPinIcon = (iconUrl, text = "", color = "black") => {
  const html = `
    <div style='text-align: center; position: relative;'>
      <img src='${iconUrl}' style='width: ${ICON_SIZES.customPin.width}px; height: ${ICON_SIZES.customPin.height}px;' />
      <div style='font-size: 12px; color: ${color}; border: 2px dotted red; display: inline-block; padding: 2px 4px; white-space: nowrap;'>${text}</div>
    </div>
  `;

  return L.divIcon({
    html: html,
    className: "custom-icon",
    iconSize: [ICON_SIZES.customPin.width, ICON_SIZES.customPin.height],
    iconAnchor: [ICON_SIZES.customPin.width / 2, ICON_SIZES.customPin.height],
  });
};

/**
 * HTML + dimensions for a station marker (pigeon-maps / Leaflet).
 * @param {string} iconUrl - unused; kept for API parity with Leaflet helper
 */
export const getStationMarkerHtml = (
  iconUrl,
  stationName,
  info,
  hasError = false,
  status,
  stationType
) => {
  let iconColor;
  let statusText;
  let textColor;
  let opacity = 1;

  const stationIconConfig = getStationIcon(stationType);

  switch (status) {
    case "Non-Operational":
      statusText = "Non-Operational";
      textColor = STATUS_TEXT_COLORS.nonOperational;
      opacity = 0.6;
      iconColor = STATUS_COLORS.nonOperational;
      break;
    case "Warning":
      iconColor = STATUS_COLORS.warning;
      statusText = "Warning";
      textColor = STATUS_TEXT_COLORS.warning;
      break;
    case "Operational":
      iconColor = STATUS_COLORS.operational;
      statusText = info || "Operational";
      textColor = STATUS_TEXT_COLORS.operational;
      break;
    default:
      iconColor = STATUS_COLORS.unknown;
      statusText = "Unknown Status";
      textColor = STATUS_TEXT_COLORS.unknown;
      opacity = 0.7;
  }

  if (hasError) {
    iconColor = STATUS_COLORS.error;
    statusText = "Error";
    textColor = STATUS_TEXT_COLORS.error;
    opacity = 0.6;
  }

  const errorIndicator =
    hasError || status === "Non-Operational"
      ? `
      <div style="
        position: absolute;
        top: -5px;
        right: -5px;
        background-color: ${iconColor};
        color: white;
        border-radius: 50%;
        width: 16px;
        height: 16px;
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">×</div>
    `
      : "";

  const html = `
    <div style='text-align: center; position: relative;'>
      <div style='position: relative; display: inline-block;'>
        <img 
          src='${stationIconConfig.icon}' 
          style='
            width: ${ICON_SIZES.weatherStation.width}px; 
            height: ${ICON_SIZES.weatherStation.height}px; 
            opacity: ${opacity};
            filter: drop-shadow(0 2px 2px rgba(0,0,0,0.2));
          '
        />
        ${errorIndicator}
      </div>
      <div style='
        font-size: 10px; 
        color: #333;
        font-weight: 500;
        margin-top: 2px;
        text-shadow: 0 1px 2px rgba(255,255,255,0.8);
      '>
        ${stationName}
      </div>
      <div style='
        font-size: 8px; 
        color: ${textColor};
        background-color: rgba(255,255,255,0.9);
        padding: 1px 3px;
        border-radius: 6px;
        margin-top: 1px;
        font-weight: 500;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        display: inline-block;
        width: fit-content;
        margin-left: auto;
        margin-right: auto;
      '>
        ${statusText}
      </div>
    </div>
  `;

  return {
    html,
    width: ICON_SIZES.customIcon.width,
    height: ICON_SIZES.customIcon.height,
  };
};

/**
 * Creates a custom icon for weather stations with status indicators
 * @returns {L.DivIcon} Leaflet div icon
 */
export const createCustomIcon = (
  iconUrl,
  stationName,
  info,
  hasError = false,
  status,
  stationType
) => {
  const { html, width, height } = getStationMarkerHtml(
    iconUrl,
    stationName,
    info,
    hasError,
    status,
    stationType
  );

  return L.divIcon({
    html,
    className: "custom-icon",
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -45],
  });
};

export default {
  createCustomPinIcon,
  createCustomIcon,
  getStationMarkerHtml,
};
